import { useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { generateAppleOAuthPopupUrl, validateAppleOAuthConfig } from '../config/appleOAuth';
import { getUserTimezone } from '../utils/timezoneUtils';

const SESSION_KEY_FLOW = 'apple_oauth_flow';
const SESSION_KEY_STATE = 'apple_oauth_state';
const SESSION_KEY_USER_TYPE = 'apple_oauth_userType';
const SESSION_KEY_RETURN_TO = 'apple_oauth_returnTo';
const SESSION_KEY_RESULT = 'apple_oauth_result';

// Decide when to use the same-window redirect flow instead of the window.open
// popup flow.
//
// iPad/iPhone browsers (Safari, Chrome, Firefox — all WebKit) treat window.open
// as a new tab without a real opener relationship, so the popup-based OAuth
// flow flips popup.closed=true while the user is still on appleid.apple.com
// and the callback page can't postMessage back.
//
// The native app's React Native WebView is worse: window.open is blocked
// unless the native side enables setSupportMultipleWindows + onOpenWindow, so
// the popup either never opens ("Popup blocked") or opens with no opener and
// the callback can only fall back to a smarteco:// deep link that the native
// side must hand back into the WebView. The redirect flow stays entirely
// inside the WebView and needs no native-side support, so use it there too.
const shouldUseRedirectFlow = (): boolean => {
  if (typeof window === 'undefined') return false;
  if ((window as any).ReactNativeWebView) return true;
  const ua = navigator.userAgent || '';
  if (/iPhone|iPod|iPad/.test(ua)) return true;
  if (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return true;
  return false;
};

export const useAppleOAuth = () => {
  const { socialLogin } = useAuth();
  const userTypeRef = useRef<number>(1);
  // const firstNameRef = useRef<string>("");
  // const lastNameRef = useRef<string>("");
  const callbacksRef = useRef<{
    onSuccess?: (_message?: string) => void;
    onError?: (_error: string) => void;
  }>({});
  const expectedStateRef = useRef<string | null>(null);
  // Cleanup for the in-progress OAuth flow. Set from inside `appleLogin`,
  // called by `cancelOAuth`.
  const cleanupRef = useRef<(() => void) | null>(null);

  // const generateStateWithTimeszoneAndUserType = useCallback(() => {
  //     const timezone = getUserTimezone();
  //     const userType = userTypeRef.current;
  //     return btoa(JSON.stringify({
  //       timezone: timezone,
  //       userType: userType.toString(),
  //       }));
  // }, [userTypeRef.current, getUserTimezone]);

  const handleAppleSuccess = useCallback(
    async (idToken: string) => {

      try {
        const socialData = {
          loginType: 'apple' as const,
          accessToken: idToken,
          email: 'apple@apple.com',
          picture: undefined,
          firstName: 'Apple User',
          lastName: '',
          userType: userTypeRef.current,
          timezone: getUserTimezone(),
        };

        const result = await socialLogin(socialData as any);

        if (result.success) {
          const successMessage =
            (result as any).message || 'Account created successfully. Redirecting to dashboard...';
          callbacksRef.current.onSuccess?.(successMessage);
        } else {
          callbacksRef.current.onError?.(result.error || 'Apple login failed');
        }

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Apple login failed';
        callbacksRef.current.onError?.(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [socialLogin]
  );

  const appleLogin = useCallback(() => {
    if (!validateAppleOAuthConfig()) {
      throw new Error('Apple OAuth is not configured. Please set REACT_APP_APPLE_CLIENT_ID in your .env file.');
    }

    const { url, state } = generateAppleOAuthPopupUrl();
    expectedStateRef.current = state;

    if (shouldUseRedirectFlow()) {
      try {
        sessionStorage.setItem(SESSION_KEY_FLOW, 'redirect');
        sessionStorage.setItem(SESSION_KEY_STATE, state);
        sessionStorage.setItem(SESSION_KEY_USER_TYPE, String(userTypeRef.current));
        sessionStorage.setItem(
          SESSION_KEY_RETURN_TO,
          window.location.pathname + window.location.search
        );
      } catch {
        // sessionStorage may be unavailable in private mode; fall through to popup as a last resort.
      }
      window.location.assign(url);
      return;
    }

    const popup = window.open(url, 'apple-login', 'width=500,height=650,scrollbars=yes,resizable=yes');
    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }

    const isReactNativeWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
    let settled = false;

    const cleanup = () => {
      settled = true;
      if (checkClosed) clearInterval(checkClosed);
      try { popup.close(); } catch {console.log("Error while closing popup")}
      window.removeEventListener('message', handleMessage);
      // @ts-ignore - RN dispatches custom event
      document.removeEventListener('message', handleMessage);
      cleanupRef.current = null;
    };

    const checkClosed = isReactNativeWebView
      ? null
      : setInterval(() => {
          if (popup.closed) {
            if (settled) {
              clearInterval(checkClosed as any);
              return;
            }
            cleanup();
            callbacksRef.current.onError?.('Login was cancelled');
          }
        }, 1000);

    const handleMessage = (event: MessageEvent) => {
      if (settled) return;
      if (event.origin && event.origin !== window.location.origin) return;

      const data = typeof event.data == "string" ? JSON.parse(event.data) : event.data;

      if (data.type === 'OAUTH_SUCCESS') {
        const returnedState = data.state as string | undefined;
        if (expectedStateRef.current && returnedState && returnedState !== expectedStateRef.current) {
          cleanup();
          callbacksRef.current.onError?.('Invalid OAuth state. Please try again.');
          return;
        }

        cleanup();
        const { access_token } = data;
        handleAppleSuccess(access_token);
      } else if (data.type === 'OAUTH_ERROR') {
        cleanup();
        callbacksRef.current.onError?.(data.error || 'Apple login failed');
      }
    };

    window.addEventListener('message', handleMessage);
    // @ts-ignore - RN dispatches custom event
    document.addEventListener('message', handleMessage);

    cleanupRef.current = cleanup;
  }, [handleAppleSuccess]);

  const cancelOAuth = useCallback(() => {
    if (cleanupRef.current) cleanupRef.current();
  }, []);

  // Picks up the token stashed by the callback page after the iOS redirect
  // flow. Call this from a mount effect on pages that initiate Apple login.
  // Returns true if a pending result was found and handled (so the caller can
  // show an "in progress" state while socialLogin runs).
  const processRedirectResult = useCallback(
    (
      callbacks?: {
        onSuccess?: (_message?: string) => void;
        onError?: (_error: string) => void;
      }
    ): boolean => {
      if (typeof window === 'undefined') return false;

      let raw: string | null = null;
      try {
        raw = sessionStorage.getItem(SESSION_KEY_RESULT);
      } catch {
        return false;
      }
      if (!raw) return false;

      let expectedState: string | null = null;
      let storedUserType: string | null = null;
      try {
        sessionStorage.removeItem(SESSION_KEY_RESULT);
        expectedState = sessionStorage.getItem(SESSION_KEY_STATE);
        storedUserType = sessionStorage.getItem(SESSION_KEY_USER_TYPE);
        sessionStorage.removeItem(SESSION_KEY_STATE);
        sessionStorage.removeItem(SESSION_KEY_USER_TYPE);
        sessionStorage.removeItem(SESSION_KEY_FLOW);
        sessionStorage.removeItem(SESSION_KEY_RETURN_TO);
      } catch {
        /* ignore storage failures */
      }

      if (callbacks) callbacksRef.current = callbacks;

      if (storedUserType) {
        const ut = parseInt(storedUserType, 10);
        if (!Number.isNaN(ut)) userTypeRef.current = ut;
      }

      let parsed: { id_token?: string; state?: string; error?: string };
      try {
        parsed = JSON.parse(raw);
      } catch {
        callbacksRef.current.onError?.('Failed to process Apple sign-in result.');
        return true;
      }

      if (parsed.error) {
        callbacksRef.current.onError?.(parsed.error);
        return true;
      }

      if (expectedState && parsed.state && parsed.state !== expectedState) {
        callbacksRef.current.onError?.('Invalid OAuth state. Please try again.');
        return true;
      }

      if (!parsed.id_token) {
        callbacksRef.current.onError?.('No id_token received from Apple');
        return true;
      }

      handleAppleSuccess(parsed.id_token);
      return true;
    },
    [handleAppleSuccess]
  );

  const appleLoginWithUserType = useCallback(
    (userType: number, 
      // firstName: string, lastName: string,
       onSuccess?: (_message?: string) => void, onError?: (_error: string) => void) => {
      userTypeRef.current = userType;
      // firstNameRef.current = firstName;
      // lastNameRef.current = lastName;
      callbacksRef.current = { onSuccess, onError };
      return () => {
        appleLogin();
      };
    },
    [appleLogin]
  );

  return {
    appleLogin,
    appleLoginWithUserType,
    handleAppleSuccess,
    cancelOAuth,
    processRedirectResult,
  };
};


